import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchGlobal } from '../services/searchApi';

const BUCKETS = [
  { key: 'denials', label: 'Denials' },
  { key: 'claims',  label: 'Claims' },
  { key: 'payers',  label: 'Payers' },
  { key: 'codes',   label: 'Codes' },
];

const QUICK_LINKS = [
  { label: 'Dashboard',          href: '/' },
  { label: 'Case Builder',       href: '/case-builder' },
  { label: 'Denial Resolve',     href: '/DenialResolve' },
  { label: 'Appeal Builder',     href: '/AppealBuilder' },
  { label: 'Claims',             href: '/claims' },
  { label: 'Payers',             href: '/payers' },
  { label: 'Contract Variance',  href: '/analytics/contract-variance' },
  { label: 'DRG / HCC',          href: '/analytics/drg-hcc' },
  { label: 'Payer Intelligence', href: '/PayerIntelligence' },
  { label: 'New intake',         href: '/intake/new' },
  { label: 'Eligibility & Auth', href: '/AuthEligibility' },
  { label: 'Coding Intelligence',href: '/CodingIntelligence' },
  { label: 'Medical Necessity',  href: '/MedicalNecessity' },
  { label: 'Claim Right scrub',  href: '/ClaimRight' },
  { label: 'Lookup tables',      href: '/admin/lookup' },
  { label: 'Billing',            href: '/billing' },
];

/**
 * Ctrl/Cmd+K global search palette.
 *
 * Renders inline in the topbar as an "open" button and as a full-screen
 * modal when active. Single GET /dm-api/search/global drives the live
 * suggestion list; QUICK_LINKS are filtered in-memory.
 */
export default function CommandPalette({ open, onOpenChange }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ denials: [], claims: [], payers: [], codes: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const close = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;
    setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(() => {
    if (!open) return undefined;
    if (!q.trim()) {
      setData({ denials: [], claims: [], payers: [], codes: [] });
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const resp = await searchGlobal(q.trim(),
          { limit: 6, signal: controller.signal });
        if (resp?.data) setData(resp.data);
      } catch (e) {
        if (e?.code !== 'ERR_CANCELED' && e?.name !== 'CanceledError') {
          setData({ denials: [], claims: [], payers: [], codes: [] });
        }
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [q, open]);

  const quickFiltered = useMemo(() => {
    if (!q.trim()) return QUICK_LINKS.slice(0, 8);
    const n = q.trim().toLowerCase();
    return QUICK_LINKS.filter((x) => x.label.toLowerCase().includes(n)).slice(0, 8);
  }, [q]);

  const go = useCallback((href) => {
    if (!href) return;
    close();
    setQ('');
    navigate(href);
  }, [close, navigate]);

  if (!open) return null;

  const totalHits = BUCKETS.reduce((acc, b) => acc + (data[b.key]?.length || 0), 0);

  return (
    <div className="dm-palette" role="dialog" aria-modal="true"
          aria-label="Global search">
      <div className="dm-palette__backdrop" onClick={close} />
      <div className="dm-palette__panel">
        <input
          ref={inputRef}
          type="search"
          className="dm-palette__input"
          placeholder="Search denials, claims, payers, codes... (Esc to close)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const first = BUCKETS
                .flatMap((b) => data[b.key] || [])
                .concat(quickFiltered.map((x) => ({ href: x.href, label: x.label })))
                [0];
              if (first?.href) go(first.href);
            }
          }}
          aria-label="Search input"
        />

        <div className="dm-palette__results">
          {loading && <div className="dm-palette__loading">Searching...</div>}

          {q.trim() && BUCKETS.map((bucket) => {
            const items = data[bucket.key] || [];
            if (!items.length) return null;
            return (
              <div className="dm-palette__group" key={bucket.key}>
                <div className="dm-palette__group-label">{bucket.label}</div>
                {items.map((it) => (
                  <button
                    type="button"
                    key={`${bucket.key}-${it.id}-${it.href}`}
                    className="dm-palette__item"
                    onClick={() => go(it.href)}
                  >
                    <span className="dm-palette__item-label">{it.label}</span>
                    {it.sublabel && (
                      <span className="dm-palette__item-sub">{it.sublabel}</span>
                    )}
                  </button>
                ))}
              </div>
            );
          })}

          <div className="dm-palette__group">
            <div className="dm-palette__group-label">
              {q.trim() ? 'Go to' : 'Jump to'}
            </div>
            {quickFiltered.map((link) => (
              <button
                type="button"
                key={link.href}
                className="dm-palette__item"
                onClick={() => go(link.href)}
              >
                <span className="dm-palette__item-label">{link.label}</span>
                <span className="dm-palette__item-sub">{link.href}</span>
              </button>
            ))}
            {!quickFiltered.length && q.trim() && totalHits === 0 && !loading && (
              <div className="dm-palette__empty">
                No matches for "{q.trim()}".
              </div>
            )}
          </div>
        </div>

        <div className="dm-palette__footer">
          <span>Enter to open</span>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}
